import type { CreateRateItemInput } from "./rateCards.types";
import { setItemCategory, setItemDescription, setItemUnit, setItemUnitPrice } from "./rateCardItems.utils";

interface RateItemsReviewTableProps {
  items: CreateRateItemInput[];
  onChange: (index: number, item: CreateRateItemInput) => void;
  onRemove: (index: number) => void;
  onAddRow: () => void;
}

function RateItemsReviewTable({ items, onChange, onRemove, onAddRow }: RateItemsReviewTableProps) {
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 w-2/5">Description</th>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 w-1/5">Category</th>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 w-1/12">Unit</th>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 w-1/6">Unit Price ($)</th>
              <th className="px-3 py-2 w-12" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {items.map((item, index) => {
              const isPriceInvalid = !Number.isFinite(item.unitPrice) || item.unitPrice < 0;
              return (
                <tr key={item.lineItemTemplate.id ?? index}>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={item.lineItemTemplate.originalLineItem}
                      onChange={(event) => onChange(index, setItemDescription(item, event.target.value))}
                      placeholder="Description"
                      className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={item.lineItemTemplate.category ?? ""}
                      onChange={(event) => onChange(index, setItemCategory(item, event.target.value))}
                      placeholder="Category"
                      className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={item.unit}
                      onChange={(event) => onChange(index, setItemUnit(item, event.target.value))}
                      placeholder="EA"
                      className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={Number.isFinite(item.unitPrice) ? item.unitPrice : 0}
                      onChange={(event) => {
                        const raw = event.target.value;
                        const parsed = raw === "" ? 0 : Number(raw);
                        onChange(index, setItemUnitPrice(item, Number.isFinite(parsed) ? parsed : 0));
                      }}
                      className={`w-full rounded-md border px-2 py-1 text-sm focus:outline-none focus:ring-2 ${
                        isPriceInvalid ? "border-red-400 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
                      }`}
                    />
                  </td>
                  <td className="px-3 py-2 align-top text-right">
                    <button
                      type="button"
                      onClick={() => onRemove(index)}
                      className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-500">
                  No items yet. Upload a PDF to extract, or add a row manually.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3">
        <button
          type="button"
          onClick={onAddRow}
          className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
        >
          Add row
        </button>
      </div>
    </div>
  );
}

export default RateItemsReviewTable;
